/**
 * SettingsScene.ts — Player settings with immediate persistence.
 *
 * Each setting cycles through options on select.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { GameSettings } from '@game/Settings';
import { theme } from '@ui/ColorTheme';
import { HelpScene } from './HelpScene';

const TEXT_SPEEDS: GameSettings['textSpeed'][] = ['instant', 'fast', 'normal', 'slow'];
const FONT_SIZES = [12, 14, 16, 18, 20];
const PARTICLE_PRESETS = ['snow', 'embers', 'arcane', 'mist'];

export class SettingsScene implements Scene {
  name = 'SettingsScene';

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    const { menu, settings, frameRenderer, effects, terminal } = ctx;

    while (true) {
      const current = settings.get();

      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'SETTINGS',
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      // Helper to format On/Off with colorblind symbols
      const formatToggle = (enabled: boolean): string => {
        const symbol = theme.getSymbolPrefix(enabled ? 'green' : 'white').trim();
        return symbol ? `[${symbol} ${enabled ? 'On' : 'Off'}]` : `[${enabled ? 'On' : 'Off'}]`;
      };

      const options: MenuOption[] = [
        {
          label: 'Text Speed:',
          color: 'white',
          suffix: `[${capitalize(current.textSpeed)}]`,
          suffixColor: 'frostSilver',
        },
        {
          label: 'Colorblind Mode:',
          color: 'white',
          suffix: formatToggle(current.colorblindMode),
          suffixColor: current.colorblindMode ? 'green' : 'white',
        },
        {
          label: 'High Contrast:',
          color: 'white',
          suffix: formatToggle(current.highContrast),
          suffixColor: current.highContrast ? 'green' : 'white',
        },
        {
          label: 'Check Animation:',
          color: 'white',
          suffix: formatToggle(current.statCheckAnimation),
          suffixColor: current.statCheckAnimation ? 'green' : 'white',
        },
        {
          label: 'Font Size:',
          color: 'white',
          suffix: `[${current.fontSize}px]`,
          suffixColor: 'frostSilver',
        },
        {
          label: 'CRT Effect:',
          color: 'white',
          suffix: formatToggle(current.crtEffect),
          suffixColor: current.crtEffect ? 'green' : 'white',
        },
        {
          label: 'Particles:',
          color: 'white',
          suffix: formatToggle(current.particles),
          suffixColor: current.particles ? 'green' : 'white',
        },
        {
          label: 'Particle Preset:',
          color: 'white',
          suffix: `[${capitalize(current.particlePreset)}]`,
          suffixColor: 'frostSilver',
        },
        {
          label: 'Terminal Frame:',
          color: 'white',
          suffix: formatToggle(current.terminalFrame),
          suffixColor: current.terminalFrame ? 'green' : 'white',
        },
        {
          label: 'Help / Guide',
          color: 'amber',
        },
      ];

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'SETTINGS',
            borderColor: 'frostBlue',
          });
        },
      });

      const { crt, particles: particleSystem, visualLayer, terminalFrame } = ctx;

      switch (sel) {
        case 0: { // Text Speed — cycle
          const currentIdx = TEXT_SPEEDS.indexOf(current.textSpeed);
          const nextIdx = (currentIdx + 1) % TEXT_SPEEDS.length;
          settings.update({ textSpeed: TEXT_SPEEDS[nextIdx] });
          break;
        }
        case 1: // Colorblind Mode — toggle
          settings.update({ colorblindMode: !current.colorblindMode });
          theme.setColorblindMode(!current.colorblindMode);
          break;
        case 2: { // High Contrast — toggle
          const newHighContrast = !current.highContrast;
          settings.update({ highContrast: newHighContrast });
          theme.setHighContrastMode(newHighContrast);
          // Disable CRT effect in high contrast mode for better readability
          if (newHighContrast) {
            crt.disable();
          } else if (current.crtEffect) {
            crt.enable();
          }
          break;
        }
        case 3: // Check Animation — toggle
          settings.update({ statCheckAnimation: !current.statCheckAnimation });
          break;
        case 4: { // Font Size — cycle
          const currentIdx = FONT_SIZES.indexOf(current.fontSize);
          const nextIdx = (currentIdx + 1) % FONT_SIZES.length;
          const newSize = FONT_SIZES[nextIdx];
          settings.update({ fontSize: newSize });
          terminal.setFontSize(newSize);
          break;
        }
        case 5: // CRT Effect — toggle
          settings.update({ crtEffect: !current.crtEffect });
          if (!current.crtEffect) crt.enable(); else crt.disable();
          break;
        case 6: // Particles — toggle
          settings.update({ particles: !current.particles });
          if (!current.particles) particleSystem.enable(); else particleSystem.disable();
          break;
        case 7: { // Particle Preset — cycle
          const currentIdx = PARTICLE_PRESETS.indexOf(current.particlePreset);
          const nextIdx = (currentIdx + 1) % PARTICLE_PRESETS.length;
          const newPreset = PARTICLE_PRESETS[nextIdx];
          settings.update({ particlePreset: newPreset });
          visualLayer.setLocationParticles(newPreset);
          break;
        }
        case 8: // Terminal Frame — toggle
          settings.update({ terminalFrame: !current.terminalFrame });
          if (!current.terminalFrame) terminalFrame.enable(); else terminalFrame.disable();
          break;
        case 9: // Help / Guide
          await engine.pushScene(new HelpScene());
          return;
        case -1: // Back
          await engine.popScene();
          return;
      }
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
